import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Landmark, Globe2, Scale, FileText, Lock, BookMarked } from "lucide-react";
import { FIRM } from "@/lib/firm";

const Section = ({ n, icon: Icon, title, children }) => (
  <section className="mb-10" data-testid={`about-sec-${n}`}>
    <div className="flex items-center gap-2.5 mb-3">
      {Icon ? <Icon size={16} className="text-[#B8860B]" /> : null}
      <h2 className="font-serif text-[19px] text-[#ECECE4]">{n}. {title}</h2>
    </div>
    <div className="text-[#9A9A90] text-[13.5px] leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function AboutUs() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4]" data-testid="about-us-page">
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between border-b border-[#1C1C16]">
        <div>
          <div className="font-serif text-[22px] tracking-tight leading-none">SEUTRADE FINANCE</div>
          <div className="font-serif text-[11px] tracking-[0.22em] uppercase text-[#B8860B] leading-none mt-1">Financial Services</div>
        </div>
        <button onClick={() => nav("/")} data-testid="about-back-btn"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#ECECE4] border border-[#2A2A22] hover:border-[#B8860B] px-4 py-2 rounded-full transition-colors">
          <ArrowLeft size={13} /> Home
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#8A8A8A] mb-5">
          <ShieldCheck size={12} className="text-[#B8860B]" /> Organizational Charter &amp; Disclosures
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.06] mb-4">
          About <span className="text-[#B8860B]">Seutrade Finance</span>
        </h1>
        <p className="text-[#9A9A90] text-base leading-relaxed mb-12 max-w-2xl">
          Seutrade Finance — Financial Services is a private international financial organization
          providing capital, refinance, and settlement services to qualified counterparties under private
          contract and the Uniform Commercial Code. This charter describes our structure, mandate, and the
          disclosures that govern every engagement.
        </p>

        <Section n="1" icon={Landmark} title="Organizational Charter">
          <p><strong className="text-[#C9C9C0]">{FIRM.name}</strong> (the <em>"Organization"</em>) is a privately
          constituted financial organization organized and operating under private law and contract, with its
          principal administrative office at {FIRM.address}. The Organization operates as a private banking
          and finance house: it originates, purchases, discounts, accepts, and negotiates commercial
          instruments and arranges private financing and refinancing for its counterparties.</p>
          <p>The Organization is governed by its charter and internal by-laws, administered by its trustees and
          authorized officers, and acts exclusively in a private, arm's-length, commercial capacity.</p>
        </Section>

        <Section n="2" icon={Globe2} title="International Structure &amp; Scope">
          <p>The Organization conducts cross-border private commercial finance with counterparties and
          correspondent institutions internationally. Instruments are structured to satisfy the negotiability
          requirements of UCC Article 3 and the acceptance/collection framework of UCC Article 4, and, where
          applicable, generally recognized international commercial practice for documentary drafts and
          banker's acceptances.</p>
          <p>Engagements are private and by application. Nothing herein constitutes a public solicitation of
          deposits or securities in any jurisdiction where such solicitation would be unlawful.</p>
        </Section>

        <Section n="3" icon={FileText} title="Mandate &amp; Services">
          <p>The Organization provides, subject to application and approval: (a) private financing and 0%-note
          refinancing; (b) asset and commercial financing; (c) note purchase, assignment, and discount in the
          private secondary market; (d) issuance and acceptance of private negotiable instruments and
          related instruments; and (e) negotiated forbearance, deferral, consolidation, and settlement
          facilitation for qualified obligations.</p>
        </Section>

        <Section n="4" icon={BookMarked} title="Legal Standing, International Law &amp; Banking Regulation">
          <p>The Organization's standing, jurisdiction, and identity rest on private law and contract together with the
          following recognized legal and commercial frameworks, which govern the form, negotiability, acceptance, and
          collection of its instruments:</p>
          <ul className="list-disc pl-5 space-y-1.5 text-[#9A9A90]">
            <li><strong className="text-[#C9C9C0]">UCC Article 3 (Negotiable Instruments)</strong> — establishes that our
            promissory notes and banker's acceptance bank notes are negotiable instruments and that a good-faith
            purchaser takes as a holder in due course (UCC §§ 3-104, 3-302, 3-409, 3-410, 3-415).</li>
            <li><strong className="text-[#C9C9C0]">UCC Article 4 (Bank Deposits &amp; Collections)</strong> — governs the
            presentment, acceptance, and collection of drafts through ordinary bank channels.</li>
            <li><strong className="text-[#C9C9C0]">UNCITRAL Model Law on International Credit Transfers</strong> — the
            United Nations framework for cross-border funds transfers, informing our international settlement practice.</li>
            <li><strong className="text-[#C9C9C0]">ICC Uniform Customs &amp; Practice for Documentary Credits (UCP 600)</strong>
            {" "}and the <strong className="text-[#C9C9C0]">ICC Uniform Rules for Collections (URC 522)</strong> — the
            International Chamber of Commerce rules widely adopted by banks worldwide for documentary credits and
            documentary collections.</li>
            <li><strong className="text-[#C9C9C0]">AML / sanctions law</strong> — 31 C.F.R. § 1010.230 customer-identification
            and beneficial-ownership rules and U.S. Treasury OFAC sanctions screening.</li>
          </ul>
          <p>Together these frameworks support the Organization's jurisdiction to originate, accept, discount, and
          negotiate private commercial instruments internationally. They confirm the private, contractual character of
          our identity — they do <strong>not</strong> make the Organization a government-chartered or
          government-guaranteed bank.</p>
          <p className="pt-1">
            <a href="/charter" data-testid="about-charter-link"
               className="inline-flex items-center gap-1.5 text-[#B8860B] hover:text-[#d6a63a] font-semibold underline underline-offset-2">
              <ShieldCheck size={14} /> View our Charter &amp; Certificate of Authority
            </a>
          </p>
        </Section>

        <Section n="5" icon={Scale} title="Compliance, AML &amp; Sanctions">
          <p>The Organization applies customer-identification (CIP) and beneficial-ownership procedures
          consistent with 31 C.F.R. § 1010.230, screens counterparties against U.S. Treasury OFAC sanctions
          lists, and declines transactions where funds are not derived from lawful sources. Each counterparty
          represents that it is not a Specially Designated National and that its funds are of lawful origin.</p>
        </Section>

        <Section n="6" icon={Lock} title="Important Disclosures &amp; Disclaimers">
          <div className="rounded-lg border border-[#3a2f12] bg-[#16130a] p-5 space-y-2.5 text-[12.5px] text-[#c9be9c]">
            <p><strong className="text-[#E4C97A]">Not a government or state/federally chartered depository bank.</strong>
            The Organization is a private financial organization. It is not the United States, a State, the
            Federal Reserve, the U.S. Treasury, or an agency or instrumentality thereof, and it does not act
            for or on behalf of any government.</p>
            <p><strong className="text-[#E4C97A]">No deposit insurance.</strong> The Organization does not accept
            insured retail deposits and its instruments and obligations are <strong>not insured or guaranteed
            by the FDIC, NCUA, SIPC, or any government agency.</strong></p>
            <p><strong className="text-[#E4C97A]">Private instruments are not legal tender or government
            obligations.</strong> A private negotiable instrument, funding certificate, draft, or
            similar instrument issued or accepted by the Organization is a private negotiable commercial
            instrument. It is not currency, not a Federal Reserve note, not a Treasury security, and not a
            government payment mechanism, and it does not by delivery alone satisfy, discharge, or release any
            third-party debt unless and until accepted funds are actually received and credited.</p>
            <p><strong className="text-[#E4C97A]">No legal, tax, or investment advice.</strong> Information here
            and in our documents is provided for commercial transactional purposes only and is not legal, tax,
            accounting, or investment advice. Counterparties should obtain independent professional counsel.</p>
            <p><strong className="text-[#E4C97A]">Subject to application &amp; approval.</strong> All financing,
            refinancing, and settlement services are offered by private application, are conditional, and are
            subject to underwriting, verification, funding availability, and applicable law.</p>
          </div>
        </Section>

        <div className="mt-12 pt-6 border-t border-[#1C1C16] text-[11px] text-[#6d6d64] leading-relaxed">
          {FIRM.name} · EIN {FIRM.ein} · {FIRM.address} · {FIRM.phone}
          <br />© {new Date().getFullYear()} {FIRM.name}. All rights reserved. Confidential — for the intended counterparty only.
        </div>
      </main>
    </div>
  );
}
